import { ContainerComponent, wrap } from 'AppReact';
import { TextButton, Rating, TextCapsule, WorkTimer } from 'molecules';
import { PrimaryTextXs, PrimaryTextXsBold, NoteText, ClickableIcon, SecondaryTextSm, Icon } from 'atoms';
import { getEventData, isNullOrUndefined, getNoteText } from 'utils';
import { EditPencilSvg, MenuDotsSvg } from 'components/icons';
import { calculateHoursBetweenDates, getAssociatedStopEvent } from 'utils/dateAndTimeUtils';
import { parseISO, format } from 'date-fns';
import { OrderEventWrapper, FullWidthFlexBoxJustifyCenterWrapper, FitRelativeWrapper, AbsoluteRightTopWrapper, BorderedCard, FlexCenterWrapper, Mr2, Mt2, HoverPrimary50BackgroundWrapper, CursorPointerWrapper, GenericWrapper } from 'wrappers';
import React from 'react';
import NoteMediaFiles from '../noteMediaFiles';
import { TimerContainer } from './containers';
import internalState from './internalState';
import controller from './controller';

const dropdownRef = React.createRef();

export const definition = {
  name: 'OrderEvent',
  controller,
  initialProps: ({ events, order_eventIndex, order_event, order_status, order_id, request_id, edit_time_entry_allowed }) => ({ timerContainerRef: React.createRef(), events, order_eventIndex, edit_time_entry_allowed, order_id, request_id, order_event, isOpen: order_event.event_name === 'created' || order_event.created_by === 'AppWork AI - BETA', order_status }),
  props: { ...internalState },
  elements: [
    {
      wrapper: FullWidthFlexBoxJustifyCenterWrapper,
      name: 'Wrapper',
      elements: [
        {
          component: wrap(PrimaryTextXsBold, Mr2),
          name: 'BoldText',
          displayValue: ({ order_event }) => getEventData(order_event).boldText ?? ''
        },
        {
          component: wrap(PrimaryTextXs, Mr2),
          name: 'TimeStamp',
          displayValue: ({ order_event }) => getEventData(order_event).timeStamp ?? ''
        },
        {
          component: PrimaryTextXs,
          name: 'Creator',
          displayValue: ({ order_event }) => getEventData(order_event).by ?? ''
        }
      ]
    },
    {
      component: wrap(TextCapsule, Mt2),
      name: 'StartTime',
      renderIf: ({ order_event, order_eventIndex, events }) => {
        const event = getAssociatedStopEvent({ index: order_eventIndex, events });
        return !isNullOrUndefined(order_eventIndex) && order_event.event_name === 'start_timer' && !event;
      },
      bindings: ({ order_event, order_eventIndex, events, startTimerWatch }) => {
        if (isNullOrUndefined(order_eventIndex)) return { value: '' };
        const event = getAssociatedStopEvent({ index: order_eventIndex, events });
        if (!event && !!startTimerWatch) return { value: startTimerWatch };
        return { value: '' };
      }
    },
    {
      wrapper: wrap(FlexCenterWrapper, Mt2),
      name: 'AddAndStopTimerWrapper',
      renderIf: ({ order_event, isOpen, isEditMode }) => isOpen && !isEditMode && ['add_timer', 'stop_timer'].includes(order_event.event_name),
      elements: [
        {
          component: wrap(WorkTimer, Mr2),
          name: 'AddAndStopTimer',
          bindings: ({ order_event }) => {
            const { startDateTime, endDateTime } = getEventData(order_event);
            const { total } = calculateHoursBetweenDates({ startDate: startDateTime, endDate: endDateTime, withSeconds: true });
            const startTime = startDateTime ? format(parseISO(startDateTime), 'hh:mm aa') : 'NA';
            const endTime = endDateTime ? format(parseISO(endDateTime), 'hh:mm aa') : 'NA';
            const date = startDateTime ? format(parseISO(startDateTime), 'MM/dd/yy') : 'NA';
            const timeSummery = `Start time: ${startTime} | End time: ${endTime} | ${date}  | `;
            return { total: `${total} hours`, time: timeSummery };
          }
        },
        {
          component: ClickableIcon,
          name: 'ActionDropdownButton',
          displayValue: () => MenuDotsSvg({}),
          renderIf: ({ order_status, edit_time_entry_allowed }) => edit_time_entry_allowed,
          bindings: ({ isActionDropdownOpen, timerContainerRef }) => ({ timerContainerRef, isActionDropdownOpen, dropdownRef })
        },
        {
          wrapper: FitRelativeWrapper,
          name: 'OuterWrapper',
          elements: [
            {
              wrapper: AbsoluteRightTopWrapper,
              name: 'Body',
              ref: dropdownRef,
              renderIf: ({ isActionDropdownOpen }) => isActionDropdownOpen,
              elements: [
                {
                  wrapper: BorderedCard,
                  displayState: () => ({ background: '#FFFFFF', borderRadius: '12px', padding: '0px' }),
                  name: 'Wrapper',
                  elements: [
                    {
                      wrapper: wrap(wrap(wrap(FlexCenterWrapper, HoverPrimary50BackgroundWrapper), CursorPointerWrapper), Mt2),
                      name: 'Edit',
                      isButton: true,
                      bindings: ({ isEditMode, timerContainerRef }) => ({ isEditMode, timerContainerRef }),
                      elements: [
                        {
                          component: wrap(Icon, Mr2),
                          name: 'Icon',
                          displayValue: () => EditPencilSvg({ width: 13, height: 13 })
                        },
                        {
                          component: SecondaryTextSm,
                          name: 'Text',
                          displayValue: () => 'Edit'
                        }
                      ]
                    }
                  ]
                }
              ]
            }
          ]
        }
      ]
    },
    {
      component: wrap(TimerContainer, Mt2),
      name: 'TimerContainer',
      renderIf: ({ order_event, isOpen, isEditMode }) => isEditMode && ['add_timer', 'stop_timer'].includes(order_event.event_name),
      bindings: ({ order_event, order_id, request_id }) => {
        const { startDateTime, endDateTime, event_id } = getEventData(order_event);
        return { startDateTime, endDateTime, event_id, order_id, request_id, showOnTop: true };
      }
    },
    {
      component: wrap(Rating, Mt2),
      name: 'Rating',
      renderIf: ({ order_event }) => order_event.event_name === 'rating',
      bindings: ({ order_event }) => ({ rating: order_event.level ?? 0 })
    },
    {
      component: wrap(NoteMediaFiles, Mt2),
      name: 'Image',
      renderIf: ({ order_event, isOpen }) => !!getEventData(order_event).attachments && isOpen,
      bindings: ({ order_event }) => ({
        files: getEventData(order_event).attachments,
        popup: { text: 'Click to see full screen', position: 'top' }
      })
    },
    {
      component: wrap(NoteText, Mt2),
      name: 'Creator',
      renderIf: ({ isOpen }) => isOpen,
      displayValue: ({ order_event, translated, translation }) => {
        if (translated) {
          return getNoteText(translation, order_event.created_by);
        }
        return getNoteText(getEventData(order_event).description ?? '', order_event.created_by);
      }
    },
    {
      component: wrap(TextButton, Mt2),
      name: 'TranslateButton',
      renderIf: ({ order_event, isOpen }) => !!order_event.description && isOpen,
      bindings: ({ translated, order_event, translation }) => ({ mobileSmall: true, title: translated ? 'Translate' : 'Traducir', translated, order_event, translation })
    },
    {
      wrapper: GenericWrapper,
      ref: ({ timerContainerRef }) => timerContainerRef,
      name: 'ButtonWrapper',
      elements: [
        {
          component: wrap(TextButton, Mt2),
          name: 'Button',
          renderIf: ({ order_event }) => ['stop_timer', 'add_timer', 'post_error_cleared'].includes(order_event.event_name) || (getEventData(order_event).attachments?.length ?? 0) > 0 || getEventData(order_event).description,
          bindings: ({ isOpen }) => ({ mobileSmall: true, isOpen, title: isOpen ? 'See less' : 'See more' })
        }
      ]
    }
  ]
};

export default wrap(ContainerComponent(definition), OrderEventWrapper);
